import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProviderChip } from '../agent/ProviderChip';
import { useAgent } from '../../store/agent';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useQueryClient } from '@tanstack/react-query';
import { Menu, PanelRight, Search } from 'lucide-react';
import { useUi } from '../../store/ui';
import { useHotkeys } from '../../hooks/useHotkeys';
import { useVaultEvents, qk } from '../../hooks/useVault';
import { Api } from '../../lib/api';
import { LeftRail } from './LeftRail';
import { RightDock } from './RightDock';
import { ErrorBoundary } from './ErrorBoundary';
import { StatusBar } from './StatusBar';
import { NoteView } from '../editor/NoteView';
import { TypesView } from '../types/TypesView';
import { SearchView } from '../search/SearchView';
import { GraphView } from '../graph/GraphView';
import { AdminView } from '../admin/AdminView';
import { CasesView } from '../cases/CasesView';
import { InsightsView } from '../insights/InsightsView';
import { IngestDialog } from '../ingest/IngestDialog';
import { CommandPalette, type Command } from '../palette/CommandPalette';
import { NewNoteDialog } from '../dialogs/NewNoteDialog';
import { Toasts } from '../ui/Toasts';
import { useSessionContext } from '../../hooks/sessionContext';

function isMobile(): boolean {
  return window.matchMedia('(max-width: 900px)').matches;
}

export function AppShell() {
  const ui = useUi();
  const qc = useQueryClient();
  const session = useSessionContext();
  const [newNote, setNewNote] = useState(false);
  const [ingest, setIngest] = useState(false);
  useVaultEvents();

  const activePane = ui.panes.find((p) => p.id === ui.activePaneId) ?? ui.panes[0];

  const commands = useMemo<Command[]>(
    () => [
      { id: 'new', title: 'New note', hint: 'Ctrl+N', run: () => setNewNote(true) },
      { id: 'split', title: 'Split pane', hint: 'Ctrl+\\', run: () => ui.splitPane() },
      { id: 'close', title: 'Close pane', hint: 'Ctrl+W', run: () => ui.closePane(ui.activePaneId) },
      { id: 'mode', title: 'Cycle edit / split / preview', hint: 'Ctrl+E', run: () => ui.cyclePaneMode(ui.activePaneId) },
      { id: 'left', title: 'Show or hide the sidebar', hint: 'Ctrl+Shift+L', run: () => ui.toggleLeft() },
      { id: 'left-collapse', title: ui.leftCollapsed ? 'Expand the sidebar' : 'Collapse the sidebar to icons', run: () => ui.toggleLeftCollapsed() },
      { id: 'right', title: 'Toggle right dock', hint: 'Ctrl+Shift+B', run: () => ui.toggleRight() },
      { id: 'full', title: 'Expand the dock across the screen', hint: 'Ctrl+Shift+Enter', run: () => ui.toggleDockFull() },
      { id: 'backlinks', title: 'Show backlinks', run: () => ui.setDockTab('backlinks') },
      { id: 'props', title: 'Show properties', run: () => ui.setDockTab('properties') },
      { id: 'outline', title: 'Show outline', run: () => ui.setDockTab('outline') },
      { id: 'types', title: 'Open note types', run: () => ui.setView('types') },
      { id: 'search', title: 'Open search view', run: () => ui.setSearch(ui.searchQuery) },
      { id: 'graph', title: 'Open graph', run: () => ui.setGraphMode('global') },
      { id: 'graph-entity', title: 'Open entity topology', run: () => ui.setGraphMode('entity') },
      { id: 'localgraph', title: 'Show local graph', run: () => ui.setDockTab('graph') },
      { id: 'agent', title: 'Open agent', hint: 'Ctrl+J', run: () => (ui.setDockTab('agent'), setTimeout(() => document.dispatchEvent(new CustomEvent('cortex:ask', { detail: '' })), 50)) },
      { id: 'proposals', title: 'Open proposals inbox', run: () => ui.setDockTab('proposals') },
      { id: 'cases', title: 'Open cases', run: () => ui.setView('cases') },
      { id: 'insights', title: 'Open insight inbox', run: () => ui.setView('insights') },
      ...(session.user?.role !== 'VIEWER' ? [{ id: 'ingest', title: 'Ingest a document (PDF, DOCX, XLSX, image, URL)', hint: 'Ctrl+I', run: () => setIngest(true) }] : []),
      { id: 'faultfind', title: 'Start fault-finding (agent, Faultfind mode)', run: () => (useAgent.getState().setMode('FAULTFIND'), ui.setDockTab('agent'), setTimeout(() => document.dispatchEvent(new CustomEvent('cortex:ask', { detail: '' })), 50)) },
      ...(session.user?.role === 'ADMIN' ? [{ id: 'admin', title: 'Open admin (credentials, tool policy, audit)', run: () => ui.setView('admin') }] : []),
      { id: 'vault', title: 'Back to vault', run: () => ui.setView('vault') },
      {
        id: 'copypath',
        title: 'Copy note path',
        run: () => {
          const id = activePane?.noteId;
          const n = id ? (qc.getQueryData(qk.note(id)) as { path?: string } | undefined) : undefined;
          if (n?.path) void navigator.clipboard.writeText(n.path).then(() => ui.toast('Path copied'));
        },
      },
      {
        id: 'reload',
        title: 'Reload note from disk (discard unsaved)',
        run: () => {
          const id = activePane?.noteId;
          if (!id) return;
          ui.dropDraft(id);
          qc.invalidateQueries({ queryKey: qk.note(id) });
        },
      },
      {
        id: 'reindex',
        title: 'Reindex vault (incremental)',
        run: () => void Api.vault.reindex(false).then((s) => (ui.toast(`Reindexed: ${s.indexed} changed, ${s.unchanged} unchanged`), qc.invalidateQueries())),
      },
      {
        id: 'reindex-full',
        title: 'Reindex vault (full rebuild)',
        run: () => void Api.vault.reindex(true).then((s) => (ui.toast(`Full reindex: ${s.indexed} notes, ${s.edges} links in ${s.ms} ms`), qc.invalidateQueries())),
      },
      {
        id: 'signout',
        title: `Sign out (${session.user?.name ?? ''})`,
        run: () => {
          qc.clear();
          session.signOut();
        },
      },
      ...(session.config?.devLoginEnabled
        ? [
            {
              id: 'devrole',
              title: `Dev: switch role (now ${session.user?.role ?? ''})`,
              run: () => {
                const next = window.prompt('Role: ADMIN, ENGINEER or VIEWER', session.user?.role ?? 'ADMIN');
                if (!next) return;
                const role = next.trim().toUpperCase();
                if (role !== 'ADMIN' && role !== 'ENGINEER' && role !== 'VIEWER') return;
                void Api.auth.devLogin({ role, email: session.user?.email, name: session.user?.name }).then((r) => {
                  session.establish(r.token, r.user);
                  qc.invalidateQueries();
                  ui.toast(`Dev role: ${role}`);
                });
              },
            },
          ]
        : []),
    ],
    [ui, qc, activePane?.noteId, session],
  );

  const hotkeys = useMemo(
    () => [
      { key: 'k', ctrl: true, allowInInput: true, run: () => ui.openPalette('') },
      { key: 'p', ctrl: true, allowInInput: true, run: () => ui.openPalette('') },
      { key: 'p', ctrl: true, shift: true, allowInInput: true, run: () => ui.openPalette('>') },
      { key: 'n', ctrl: true, allowInInput: true, run: () => setNewNote(true) },
      { key: '\\', ctrl: true, allowInInput: true, run: () => ui.splitPane() },
      { key: 'e', ctrl: true, allowInInput: true, run: () => ui.cyclePaneMode(ui.activePaneId) },
      { key: 'w', ctrl: true, allowInInput: true, run: () => ui.closePane(ui.activePaneId) },
      { key: 'l', ctrl: true, shift: true, allowInInput: true, run: () => ui.toggleLeft() },
      { key: 'b', ctrl: true, shift: true, allowInInput: true, run: () => ui.toggleRight() },
      { key: 'Enter', ctrl: true, shift: true, allowInInput: true, run: () => ui.toggleDockFull() },
      // Esc leaves the expanded dock, but a dialog or the palette owns Esc while it is open.
      { key: 'Escape', allowInInput: true, run: () => ui.dockFull && !document.querySelector('.scrim, [role="dialog"]') && ui.toggleDockFull(false) },
      { key: 'j', ctrl: true, allowInInput: true, run: () => (ui.setDockTab('agent'), setTimeout(() => document.dispatchEvent(new CustomEvent('cortex:ask', { detail: '' })), 50)) },
      { key: 'i', ctrl: true, allowInInput: true, run: () => session.user?.role !== 'VIEWER' && setIngest(true) },
    ],
    [ui],
  );
  useHotkeys(hotkeys);

  const onNewNote = useCallback(() => setNewNote(true), []);
  useEffect(() => {
    const open = () => setIngest(true);
    document.addEventListener('cortex:ingest', open);
    return () => document.removeEventListener('cortex:ingest', open);
  }, []);

  return (
    <div className="shell">
      {ui.leftOpen && isMobile() && <div className="sidebar-backdrop" onClick={() => ui.toggleLeft()} />}
      <div className={`sidebar-slot${ui.leftOpen ? " open" : ""}`}>
        <LeftRail onNewNote={onNewNote} open={ui.leftOpen} collapsed={ui.leftCollapsed} />
      </div>
      <div className="shell-body">
        <div className="main">
          <div className="workspace">
            <div className="topbar">
              <button className="menu-btn" title="Menu" onClick={() => ui.toggleLeft()}><Menu size={16} /></button>
              <span className="page-title">{ui.view === "types" ? "Note types" : ui.view === "search" ? "Search" : ui.view === "graph" ? "Graph" : ui.view === "admin" ? "Admin" : ui.view === "cases" ? "Cases" : ui.view === "insights" ? "Insights" : "Vault"}</span>
              <button className="omnibox" onClick={() => ui.openPalette("")}>
                <Search size={14} /> Search or ask… <span className="kbd">Ctrl K</span>
              </button>
              <div className="topbar-right">
                <ProviderChip />
                <button className="icon" title="Toggle right dock (Ctrl+Shift+B)" onClick={() => ui.toggleRight()} style={{ color: ui.rightOpen ? "var(--color-primary)" : undefined }}><PanelRight size={16} /></button>
              </div>
            </div>
            <div style={{ minHeight: 0, position: "relative" }}>
              {ui.dockFull ? (
                <div className="dock-full">
                  <ErrorBoundary what="right dock">
                    <RightDock />
                  </ErrorBoundary>
                </div>
              ) : (
              <PanelGroup direction="horizontal" autoSaveId="cortex.center.v2">
                <Panel order={1} id="content" minSize={30}>
                  {ui.view === "types" ? (
                    <TypesView />
                  ) : ui.view === "search" ? (
                    <SearchView />
                  ) : ui.view === "graph" ? (
                    <GraphView />
                  ) : ui.view === "admin" ? (
                    <AdminView />
                  ) : ui.view === "cases" ? (
                    <CasesView />
                  ) : ui.view === "insights" ? (
                    <InsightsView />
                  ) : (
                    <PanelGroup direction="horizontal" autoSaveId="cortex.panes.v1" className="panes">
                      {ui.panes.map((p, i) => (
                        <PaneSlot key={p.id} order={i}>
                          <NoteView pane={p} active={p.id === ui.activePaneId} />
                        </PaneSlot>
                      ))}
                    </PanelGroup>
                  )}
                </Panel>
                {ui.rightOpen && (
                  <>
                    <PanelResizeHandle className="resize-handle" />
                    <Panel defaultSize={26} minSize={16} maxSize={45} order={2} id="right">
                      <ErrorBoundary what="right dock">
                        <RightDock />
                      </ErrorBoundary>
                    </Panel>
                  </>
                )}
              </PanelGroup>
              )}
              {!ui.rightOpen && !ui.dockFull && (
                <button className="dock-reopen" title="Show the agent and note panels (Ctrl+Shift+B)" onClick={() => ui.toggleRight()}>
                  <PanelRight size={14} /> <span>Agent</span>
                </button>
              )}
            </div>
          </div>
        </div>
        <StatusBar />
      </div>
      <CommandPalette commands={commands} />
      {newNote && <NewNoteDialog onClose={() => setNewNote(false)} />}
      {ingest && <IngestDialog onClose={() => setIngest(false)} />}
      <Toasts />
    </div>
  );
}

function PaneSlot({ order, children }: { order: number; children: React.ReactNode }) {
  return (
    <>
      {order > 0 && <PanelResizeHandle className="resize-handle" />}
      <Panel order={order} minSize={20} id={`pane-${order}`}>
        {children}
      </Panel>
    </>
  );
}
